import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from 'lucide-react';
import client, { apiError } from './adminApi';

const inputCls = 'w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900';

function LinkGroup({ title, links, onChange, testid }) {
  const update = (i, patch) => onChange(links.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const move = (i, dir) => {
    const next = [...links];
    const [item] = next.splice(i, 1);
    next.splice(i + dir, 0, item);
    onChange(next);
  };
  return (
    <fieldset className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <legend className="px-1 text-sm font-extrabold text-navy-900">{title}</legend>
      <div className="mt-2 space-y-2">
        {links.map((link, i) => (
          <div key={i} className="flex items-center gap-2">
            <input aria-label="Label" value={link.label} onChange={(e) => update(i, { label: e.target.value })} className={inputCls} placeholder="Label" />
            <input aria-label="Destination" value={link.href} onChange={(e) => update(i, { href: e.target.value })} className={inputCls} placeholder="/internal-path" />
            <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1.5 text-slate-400 hover:text-navy-900 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
            <button type="button" aria-label="Move down" disabled={i === links.length - 1} onClick={() => move(i, 1)} className="rounded p-1.5 text-slate-400 hover:text-navy-900 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
            <button type="button" aria-label={`Remove ${link.label}`} onClick={() => onChange(links.filter((_, j) => j !== i))} className="rounded p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...links, { label: '', href: '/' }])} data-testid={`${testid}-add`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-bold text-navy-900 hover:bg-navy-50">
          <Plus className="h-3.5 w-3.5" /> Add link
        </button>
      </div>
    </fieldset>
  );
}

// Constrained navigation management — internal links only, /admin protected
// (enforced on the backend too).
export default function NavigationPage() {
  const [nav, setNav] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.get('/admin/navigation').then((res) => setNav(res.data.navigation)).catch((err) => setError(apiError(err)));
  }, []);

  if (!nav) return <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await client.put('/admin/navigation', nav);
      setNotice('Navigation saved — header and footer update site-wide.');
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-navigation">
      <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Navigation</h1>
      <p className="mt-1 text-sm text-slate-500">
        Labels, destinations and order. Internal paths only — admin routes and external scripts are rejected.
      </p>
      <div className="mt-6 space-y-5">
        <LinkGroup title="Header menu" links={nav.header || []} onChange={(l) => setNav({ ...nav, header: l })} testid="nav-header" />
        <LinkGroup title="Footer — Company" links={nav.footerCompany || []} onChange={(l) => setNav({ ...nav, footerCompany: l })} testid="nav-footer-company" />
        <LinkGroup title="Footer — Services" links={nav.footerServices || []} onChange={(l) => setNav({ ...nav, footerServices: l })} testid="nav-footer-services" />
      </div>
      {notice && <p role="status" className="mt-4 rounded-md border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-semibold text-teal-800" data-testid="nav-notice">{notice}</p>}
      {error && <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700" data-testid="nav-error">{error}</p>}
      <button type="button" onClick={save} disabled={busy} data-testid="nav-save" className="mt-5 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white hover:bg-navy-700 disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Save Navigation
      </button>
    </div>
  );
}
